import Alpine from 'alpinejs'
import axios from 'axios'
import {
  createIcons,
  AppWindow, Archive, ArrowLeft, Award, BadgeCheck, Ban, BookOpen, Building, Building2, ChartNoAxesColumn, Check,
  ChevronDown, ChevronLeft, ChevronRight, Circle, CircleAlert, CircleCheck, CircleDashed, CircleDot,
  CirclePause, CircleUser, CircleX,
  Clock, CloudAlert, Code, Copy, CreditCard, EllipsisVertical, ExternalLink, Hand,
  History, House, Infinity as InfinityIcon, Info, KeyRound, Laptop, LayoutGrid, LifeBuoy, Link, Lock, LogOut, Mail,
  MessageSquareText, Minus, Monitor, Pause, Play, Plus, Power, RefreshCw, RotateCw, SatelliteDish, Save,
  Search, SearchX, Send, Settings, Shield, ShieldAlert, ShieldCheck, ShieldX, SlidersHorizontal, Smartphone,
  SquarePen, Terminal, Trash2, TriangleAlert, User, UserMinus, UserPlus, Users, Workflow, Wrench, X, Zap,
} from 'lucide'

window.axios = axios
window.Alpine = Alpine

// Only the icons actually referenced by the identity templates are bundled so the
// vendor payload stays small. Keep this list in sync with the data-lucide names used.
const lucideIcons = {
  AppWindow, Archive, ArrowLeft, Award, BadgeCheck, Ban, BookOpen, Building, Building2, ChartNoAxesColumn, Check,
  ChevronDown, ChevronLeft, ChevronRight, Circle, CircleAlert, CircleCheck, CircleDashed, CircleDot,
  CirclePause, CircleUser, CircleX,
  Clock, CloudAlert, Code, Copy, CreditCard, EllipsisVertical, ExternalLink, Hand,
  History, House, Infinity: InfinityIcon, Info, KeyRound, Laptop, LayoutGrid, LifeBuoy, Link, Lock, LogOut, Mail,
  MessageSquareText, Minus, Monitor, Pause, Play, Plus, Power, RefreshCw, RotateCw, SatelliteDish, Save,
  Search, SearchX, Send, Settings, Shield, ShieldAlert, ShieldCheck, ShieldX, SlidersHorizontal, Smartphone,
  SquarePen, Terminal, Trash2, TriangleAlert, User, UserMinus, UserPlus, Users, Workflow, Wrench, X, Zap,
}

// Replace <i data-lucide="…"> placeholders with inline SVGs. Sizing/coloring is
// handled by CSS (.lucide { width: 1em; height: 1em }) so the icons inherit the
// font-size and currentColor of whatever they sit in — matching how the icons they
// replaced behaved.
function renderIcons() {
  createIcons({ icons: lucideIcons, attrs: { 'stroke-width': 1.75 } })
  warnUnbundledIcons()
}

// createIcons() replaces every resolved <i data-lucide="…"> with an inline <svg>.
// A name that isn't in the bundle above is left untouched and silently renders a
// blank gap. In dev, surface those so an unbundled icon fails loudly instead of
// shipping an empty slot. Tree-shaken out of the minified production build.
const warnedIcons = new Set()
function warnUnbundledIcons() {
  if (!import.meta.env.DEV) return
  for (const el of document.querySelectorAll('i[data-lucide]')) {
    const name = el.getAttribute('data-lucide')
    if (!name || warnedIcons.has(name)) continue
    warnedIcons.add(name)
    console.warn(`[icons] data-lucide="${name}" is not bundled in libs.js — add it to the imports and lucideIcons map.`)
  }
}

// Alpine renders lists/modals/dropdowns after the initial paint (x-for, x-if,
// x-show reveals). Re-run icon rendering when the DOM changes, coalesced to one
// pass per frame so it stays cheap.
let iconFrame = null
function scheduleRenderIcons() {
  if (iconFrame !== null) return
  iconFrame = requestAnimationFrame(() => {
    iconFrame = null
    renderIcons()
  })
}


// Status card maintenance windows are rendered server-side in UTC and rewritten here in the
// viewer's own timezone (DEC-009). Neither Login nor Portal has an organization or facility
// context, so Takt's usual facility timezone is unavailable — and the viewer's own clock is the
// right answer anyway: this is someone deciding whether maintenance lands during their shift.
//
// Progressive enhancement, not client-side rendering: with scripting unavailable the server's UTC
// string stands, which is correct, just not localised. The zone is named in both cases — a time in
// an unstated zone is worse than no time.
function localiseMaintenanceWindows() {
  for (const el of document.querySelectorAll('time[data-status-window-start]')) {
    const text = localisedWindow(
      el.getAttribute('data-status-window-start'),
      el.getAttribute('data-status-window-end'),
    )

    if (text) el.textContent = text
  }
}

// localisedWindow renders a window in the browser's resolved timezone, or returns null when the
// timestamps or the Intl APIs are unusable — in which case the server's UTC text is left alone.
function localisedWindow(startISO, endISO) {
  if (!startISO) return null

  try {
    const start = new Date(startISO)
    if (Number.isNaN(start.getTime())) return null

    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const date = new Intl.DateTimeFormat(undefined, {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: zone,
    })
    // timeZoneName gives the viewer's own zone abbreviation, so the rendered string always says
    // which clock it is in.
    const time = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit', minute: '2-digit', timeZoneName: 'short', timeZone: zone,
    })
    const timeNoZone = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit', minute: '2-digit', timeZone: zone,
    })

    if (!endISO) return `from ${date.format(start)}, ${time.format(start)}`

    const end = new Date(endISO)
    if (Number.isNaN(end.getTime())) return `from ${date.format(start)}, ${time.format(start)}`

    // Same local day collapses to one date; spanning days names both.
    if (date.format(start) === date.format(end)) {
      return `on ${date.format(start)}, ${timeNoZone.format(start)}\u2013${time.format(end)}`
    }

    return `from ${date.format(start)}, ${time.format(start)} to ${date.format(end)}, ${time.format(end)}`
  } catch {
    return null
  }
}

Alpine.start()

renderIcons()
localiseMaintenanceWindows()
document.addEventListener('alpine:initialized', renderIcons)
new MutationObserver(scheduleRenderIcons).observe(document.body, {
  childList: true,
  subtree: true,
})
